import React, { useContext, useEffect, useState } from 'react';
import { TeamContext } from 'contexts';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import isURL from 'validator/es/lib/isURL';
import { Title, Form, Input, Button, message, Spin } from 'components';
import { doesTeamMlsIdExist, update as updateTeam } from 'api/TeamController';

interface DetailsTabProps {
  navigateBack: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  navigateBack,
}: DetailsTabProps) => {
  const { targetTeamState, loading, saving, setSaving, fetchTeam } =
    useContext(TeamContext);

  const [teamName, setTeamName] = React.useState<string>('');
  const [teamMlsId, setTeamMlsId] = React.useState<string>('');
  const [teamEmail, setTeamEmail] = React.useState<string>('');
  const [teamWebsite, setTeamWebsite] = React.useState<string>('');
  const [teamPhone, setTeamPhone] = React.useState<string>('');

  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [isValidURL, setIsValidURL] = useState<boolean>(true);
  const [isValidTeamMlsId, setIsValidTeamMlsId] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const isSaveDisabled =
    !teamName || !isValidEmail || !isValidURL || !isValidTeamMlsId;

  const handleSave = async () => {
    const isTeamMlsIdDuplicate = teamMlsId
      ? await doesTeamMlsIdExist(targetTeamState.id, teamMlsId)
      : false;
    if (isTeamMlsIdDuplicate) {
      setIsValidTeamMlsId(false);
      return message.error(
        'Failed to save: Team with the same Team MLS ID already exists.',
      );
    }
    setSaving(true);
    try {
      const result = await updateTeam(targetTeamState.id, {
        ...targetTeamState,
        name: teamName,
        teamMlsId,
        email: teamEmail,
        website: teamWebsite,
        phone: teamPhone,
      });
      if (result.status === 200) {
        message.success('Team Details updated successfully.');
        fetchTeam();
        setErrorMessage('');
      }
      setSaving(false);
    } catch (err) {
      message.error('Something went wrong.');
      setSaving(false);
      console.error(err);
    }
  };

  useEffect(() => {
    setTeamName(targetTeamState.name);
    setTeamMlsId(targetTeamState.teamMlsId);
    setTeamEmail(targetTeamState.email);
    setTeamWebsite(targetTeamState.website);
    setTeamPhone(targetTeamState.phone);
  }, [targetTeamState]);

  return (
    <>
      {loading ? (
        <Spin center size="large" />
      ) : (
        <Form layout="vertical">
          <StyledTitle level={3}>Details</StyledTitle>
          <Form.Item>
            <Input
              label="Team Name"
              data-cy="team-input-name"
              placeholder="Team Name"
              value={teamName}
              required
              onChange={({ target: { value } }) => setTeamName(value)}
            />
          </Form.Item>
          <Form.Item
            label="Team MLS ID"
            validateStatus={!isValidTeamMlsId ? 'error' : ''}
            help={
              !isValidTeamMlsId &&
              'Team MLS ID must be unique (this ID is already in use).'
            }
          >
            <Input
              data-cy="team-input-mls-id"
              placeholder="Team MLS ID"
              value={teamMlsId}
              onChange={({ target: { value } }) => {
                if (!isValidTeamMlsId) setIsValidTeamMlsId(true);
                setTeamMlsId(value);
              }}
            />
          </Form.Item>
          <Form.Item
            label="Team Email"
            validateStatus={!isValidEmail || errorMessage ? 'error' : ''}
            help={
              errorMessage || (!isValidEmail && 'Please provide a valid email')
            }
          >
            <Input
              data-cy="team-input-email"
              placeholder="Team Email"
              value={teamEmail}
              onChange={({ target: { value } }) => {
                if (
                  !value &&
                  targetTeamState?.email &&
                  targetTeamState.sendNotificationToTeamEmail
                ) {
                  setErrorMessage(
                    'Removing email will also turn off all notifications to team email ',
                  );
                } else {
                  setErrorMessage('');
                }
                setTeamEmail(value);
                setIsValidEmail(value ? isEmail(value) : true);
              }}
            />
          </Form.Item>
          <Form.Item
            label="Team Website"
            validateStatus={!isValidURL ? 'error' : ''}
            help={!isValidURL && 'Please provide a valid website'}
          >
            <Input
              data-cy="team-input-website"
              placeholder="Team Website"
              value={teamWebsite}
              onChange={({ target: { value } }) => {
                setTeamWebsite(value);
                setIsValidURL(value ? isURL(value) : true);
              }}
            />
          </Form.Item>
          <Form.Item label="Team Phone">
            <Input
              data-cy="team-input-phone"
              placeholder="555-555-5555"
              value={teamPhone}
              onChange={({ target: { value } }) => {
                setTeamPhone(value);
              }}
            />
          </Form.Item>
          <Form.Item>
            <StyledButton
              type="primary"
              onClick={handleSave}
              data-cy="save-button"
              disabled={isSaveDisabled}
              loading={saving}
            >
              Save Changes
            </StyledButton>
            <StyledButton onClick={navigateBack} data-cy="cancel-button">
              Cancel
            </StyledButton>
          </Form.Item>
        </Form>
      )}
    </>
  );
};

export default DetailsTab;

const StyledTitle = styled(Title)`
  /* !important to override antd default margin of 0.5em */
  margin-bottom: 24px !important;
`;

const StyledButton = styled(Button)`
  margin-right: 16px;
`;
